'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { Container } from '@/components/ui/Section';
import { getPrimaryNav, ROUTES } from '@/config/routes';
import { useSessionFlag } from '@/hooks/useSessionFlag';
import { cn } from '@/lib/cn';

import { Logo } from './Logo';

function navClass(isActive: boolean, mobile = false) {
  if (mobile) {
    return cn(
      'rounded-xl px-4 py-3 text-base font-medium transition',
      isActive ? 'bg-brand/15 text-white' : 'text-content-muted hover:bg-white/5 hover:text-white',
    );
  }

  return cn(
    'rounded-full px-4 py-2 text-sm font-medium transition',
    isActive ? 'bg-white/10 text-white' : 'text-content-muted hover:bg-white/5 hover:text-white',
  );
}

function pathMatches(pathname: string | null | undefined, href: string) {
  const path = pathname || '/';
  if (href === '/') return path === '/';
  return path === href || path.startsWith(`${href}/`);
}

function AccountChip({ label, className }: { label: string; className?: string }) {
  return (
    <Link
      href={ROUTES.dashboard}
      className={cn(
        'border-line/80 hover:border-brand/40 hover:bg-brand/10 inline-flex max-w-[14rem] items-center gap-2 rounded-full border px-3 py-1.5 transition',
        className,
      )}
      title={`Open dashboard — signed in as ${label}`}
      aria-label={`Dashboard (${label})`}
    >
      <span
        aria-hidden="true"
        className="bg-brand/25 text-brand-bright inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold uppercase"
      >
        {label.slice(0, 1)}
      </span>
      <span className="min-w-0 text-left">
        <span className="text-content-subtle block text-[10px] leading-none font-semibold tracking-wider uppercase">
          Dashboard
        </span>
        <span className="block truncate text-sm font-medium text-white">{label}</span>
      </span>
    </Link>
  );
}

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { isReady, isAuthenticated, accountLabel, signOut } = useSessionFlag();
  const pathname = usePathname();

  // SSR + first client paint stay on the logged-out chrome. Only switch after
  // sessionStorage is read so we never hydrate a mismatch into the header.
  const showAccount = isReady && isAuthenticated && Boolean(accountLabel);
  const primaryNav = getPrimaryNav(showAccount);

  useEffect(() => setIsMenuOpen(false), [pathname]);

  // Prefer a late-loaded scrolled style via CSS alone on first paint; JS only
  // toggles after idle so it never competes with LCP on mobile.
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setIsScrolled(window.scrollY > 12));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition duration-300',
        isScrolled || isMenuOpen ? 'border-line/80 bg-ink/90 border-b backdrop-blur-xl' : 'border-b border-transparent',
      )}
    >
      <Container className="flex h-18 items-center justify-between gap-4 py-3">
        <Logo />

        <nav aria-label="Primary" className="hidden items-center gap-1 lg:flex">
          {primaryNav.map((link) => (
            <Link key={link.to} href={link.to} className={navClass(pathMatches(pathname, link.to))}>
              {link.label}
            </Link>
          ))}
          {showAccount ? (
            <Link
              href={ROUTES.dashboard}
              className={navClass(pathMatches(pathname, ROUTES.dashboard))}
            >
              Dashboard
            </Link>
          ) : null}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          {showAccount ? (
            <>
              <AccountChip label={accountLabel!} />
              <Button variant="ghost" size="sm" onClick={signOut}>
                Sign out
              </Button>
            </>
          ) : (
            <Button variant="secondary" size="sm" to={ROUTES.login}>
              Operator login
            </Button>
          )}
          <Button size="sm" to={ROUTES.buy}>
            Get a LaunchPad
          </Button>
        </div>

        <button
          type="button"
          onClick={() => setIsMenuOpen((open) => !open)}
          aria-expanded={isMenuOpen}
          aria-controls="mobile-nav"
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          className="border-line-strong text-content inline-flex h-11 w-11 items-center justify-center rounded-xl border lg:hidden"
        >
          <Icon name={isMenuOpen ? 'close' : 'menu'} />
        </button>
      </Container>

      {isMenuOpen ? (
        <div
          id="mobile-nav"
          className="border-line/80 bg-ink/98 max-h-[calc(100dvh-4.5rem)] overflow-y-auto border-t backdrop-blur-xl lg:hidden"
        >
          <Container className="flex flex-col gap-2 py-6">
            {primaryNav.map((link) => (
              <Link key={link.to} href={link.to} className={navClass(pathMatches(pathname, link.to), true)}>
                {link.label}
              </Link>
            ))}
            {showAccount ? (
              <Link
                href={ROUTES.dashboard}
                className={navClass(pathMatches(pathname, ROUTES.dashboard), true)}
              >
                Dashboard
              </Link>
            ) : null}

            <div className="mt-3 flex flex-col gap-3">
              {showAccount ? (
                <>
                  <AccountChip label={accountLabel!} className="max-w-none w-full justify-start rounded-xl px-4 py-3" />
                  <Button variant="secondary" fullWidth onClick={signOut}>
                    Sign out
                  </Button>
                </>
              ) : (
                <Button variant="secondary" fullWidth to={ROUTES.login}>
                  Operator login
                </Button>
              )}
              <Button fullWidth to={ROUTES.buy}>
                Get a LaunchPad
              </Button>
            </div>
          </Container>
        </div>
      ) : null}
    </header>
  );
}
